// Workforce composition rule builder.
//
// Crustdata's CompanyDB exposes per-role distribution metrics under
// nested column paths:
//   role_distribution_percent.<RoleName>     — current % of HC
//   role_growth_6m.<role>                    — 6m % growth in that role
//   role_growth_yoy.<role>                   — YoY % growth in that role
//
// This builder lets users add one or more rules like
// "Engineering ≥ 20%", "Sales YoY growth ≥ 50%". Each rule emits a
// CompanyDB filter condition under the right dotted path.
//
// Stored on CompanySearchFilters as a list since users routinely
// stack multiple constraints ("≥30% Eng AND ≥15% Sales").

import { AppIcon } from '@/components/ui/app-icon';
import * as React from 'react';
import { Badge } from '@/components/ui/badge';

type IconShim = React.ComponentType<{ className?: string }>;
const Plus: IconShim = (p) => <AppIcon icon={iconPlus} {...p} />;
const X: IconShim = (p) => <AppIcon icon={iconXmark} {...p} />;
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { iconPlus, iconXmark } from '@/lib/icons';

// Crustdata role names. Matches the LinkedIn function taxonomy that
// Crustdata uses to roll up employee profiles. The list isn't
// exhaustive (Crustdata's full set is open-ended) — these are the
// roles that surface meaningfully on a typical SaaS / B2B account.
export const WORKFORCE_ROLES = [
  'Engineering',
  'Sales',
  'Marketing',
  'Operations',
  'Finance',
  'Human Resources',
  'Product Management',
  'Customer Success',
  'Information Technology',
  'Research',
  'Consulting',
  'Legal',
  'Business Development',
] as const;

export type WorkforceMetric = 'percent' | 'growth_6m' | 'growth_yoy';

export interface WorkforceRule {
  role: string;
  metric: WorkforceMetric;
  /** ``>=`` / ``<=`` — the only two comparison directions we expose,
   *  consistent with how `>=` / `<=` are written in the rest of the
   *  CompanyDB filter blob. */
  op: '>=' | '<=';
  threshold: number;
}

const METRIC_LABELS: Record<WorkforceMetric, string> = {
  percent: 'Current % of HC',
  growth_6m: '6m growth %',
  growth_yoy: 'YoY growth %',
};

export interface RolePercentRuleBuilderProps {
  value: WorkforceRule[];
  onChange: (next: WorkforceRule[]) => void;
  className?: string;
}

export function RolePercentRuleBuilder({
  value,
  onChange,
  className,
}: RolePercentRuleBuilderProps) {
  // Draft state for the row being composed. Committed to `value` on
  // explicit "Add rule" click — prevents partial entries from showing
  // up as half-complete chips while the user is still picking.
  const [draftRole, setDraftRole] = React.useState<string>('Engineering');
  const [draftMetric, setDraftMetric] =
    React.useState<WorkforceMetric>('percent');
  const [draftOp, setDraftOp] = React.useState<'>=' | '<='>('>=');
  const [draftThreshold, setDraftThreshold] = React.useState<string>('20');

  // Whether the draft was edited since the last Add rule.
  const [dirty, setDirty] = React.useState(false);

  const addRule = () => {
    const n = Number(draftThreshold);
    if (draftThreshold.trim() === '' || Number.isNaN(n)) return;
    onChange([
      ...value,
      { role: draftRole, metric: draftMetric, op: draftOp, threshold: n },
    ]);
    setDraftThreshold('20');
    setDirty(false);
  };

  // Closing the filter (Done, Escape, a click away) unmounts the builder.
  // An edited draft is a rule the user meant: add it then, rather than
  // leave a filled-in rule that was never applied.
  const latest = React.useRef({ dirty, addRule });
  latest.current = { dirty, addRule };
  React.useEffect(
    () => () => {
      if (latest.current.dirty) latest.current.addRule();
    },
    []
  );

  const removeRule = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div className={cn('space-y-2', className)}>
      {value.length > 0 && (
        <div className='flex flex-wrap gap-1.5'>
          {value.map((rule, i) => (
            <Badge
              key={`${rule.role}:${rule.metric}:${i}`}
              variant='secondary'
              className='gap-1'
            >
              {rule.role} · {METRIC_LABELS[rule.metric]} {rule.op}{' '}
              {rule.threshold}%
              <button
                type='button'
                onClick={() => removeRule(i)}
                aria-label='Remove rule'
                className='ml-0.5 rounded-full outline-hidden hover:bg-muted-foreground/20'
              >
                <X className='h-3 w-3' />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className='grid grid-cols-2 gap-1.5'>
        <Select
          value={draftRole}
          onValueChange={(v) => {
            setDraftRole(v);
            setDirty(true);
          }}
        >
          <SelectTrigger className='h-8 text-xs'>
            <SelectValue placeholder='Role' />
          </SelectTrigger>
          <SelectContent>
            {WORKFORCE_ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={draftMetric}
          onValueChange={(v) => {
            setDraftMetric(v as WorkforceMetric);
            setDirty(true);
          }}
        >
          <SelectTrigger className='h-8 text-xs'>
            <SelectValue placeholder='Metric' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='percent'>Current % of HC</SelectItem>
            <SelectItem value='growth_6m'>6m growth %</SelectItem>
            <SelectItem value='growth_yoy'>YoY growth %</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={draftOp}
          onValueChange={(v) => {
            setDraftOp(v as '>=' | '<=');
            setDirty(true);
          }}
        >
          <SelectTrigger className='h-8 text-xs'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='>='>≥ at least</SelectItem>
            <SelectItem value='<='>≤ at most</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type='number'
          value={draftThreshold}
          onChange={(e) => {
            setDraftThreshold(e.target.value);
            setDirty(true);
          }}
          placeholder='%'
          className='h-8 text-xs'
        />
      </div>

      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={addRule}
        className='h-7 w-full text-xs'
      >
        <Plus className='mr-1 h-3 w-3' />
        Add rule
      </Button>
    </div>
  );
}
